import React, { useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
  rectIntersection,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Edit2, Trash2, CalendarIcon, Clock, Eye, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { Tables } from '@/integrations/supabase/types';
import { Staff } from './SchedulerTypes';
import ViewTaskModal from './ViewTaskModal';

type StaffTodo = Tables<'staff_todos'> & { category?: string; project_id?: string };
type TaskStatus = Tables<'staff_todos'>['status'];
type Project = Tables<'projects'>;

interface TasksKanbanViewProps {
  todos: StaffTodo[];
  staff: Staff[];
  onEdit: (todo: StaffTodo) => void;
  onDelete: (id: string) => void;
  onUpdateTodo: (todo: StaffTodo) => void;
  showStaffName?: boolean;
  projects?: Project[];
  canDelete?: boolean;
}

const statusColumns: { status: TaskStatus; title: string; color: string }[] = [
  { status: 'pending', title: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  { status: 'in-progress', title: 'In Progress', color: 'bg-blue-100 text-blue-800' },
  { status: 'review', title: 'Review', color: 'bg-purple-100 text-purple-800' },
  { status: 'on-hold', title: 'On Hold', color: 'bg-orange-100 text-orange-800' },
  { status: 'completed', title: 'Completed', color: 'bg-green-100 text-green-800' },
  { status: 'cancelled', title: 'Cancelled', color: 'bg-red-100 text-red-800' },
];

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'urgent':
      return 'bg-red-100 text-red-700 border-red-300';
    case 'high':
      return 'bg-orange-100 text-orange-700 border-orange-300';
    case 'medium':
      return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    case 'low':
      return 'bg-green-100 text-green-700 border-green-300';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-300';
  }
};

interface DroppableColumnProps {
  status: TaskStatus;
  children: React.ReactNode;
}

const DroppableColumn: React.FC<DroppableColumnProps> = ({ status, children }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: status,
  });

  return (
    <div
      ref={setNodeRef}
      className={`min-h-[400px] transition-colors ${
        isOver ? 'bg-blue-50 border-2 border-blue-300 border-dashed rounded-lg' : ''
      }`}
    >
      {children}
    </div>
  );
};

interface TaskCardProps {
  todo: StaffTodo;
  staff: Staff[];
  onEdit: (todo: StaffTodo) => void;
  onView: (todo: StaffTodo) => void;
  onDelete: (id: string) => void;
  showStaffName?: boolean;
  isDragging?: boolean;
  projects?: Project[];
  canDelete?: boolean;
}

const TaskCard: React.FC<TaskCardProps> = ({
  todo,
  staff,
  onEdit,
  onView,
  onDelete,
  showStaffName,
  isDragging,
  projects = [],
  canDelete = false,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: todo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getStaffName = (staffId: string) => {
    const staffMember = staff.find(s => s.id === staffId);
    return staffMember?.name || 'Unknown';
  };

  // Check if task is overdue - compare dates only (ignore time)
  // Also determine if it's "critical" overdue (2+ extensions = red, otherwise orange)
  const { isOverdue, isCriticalOverdue } = (() => {
    if (!todo.deadline || todo.status === 'completed' || todo.status === 'cancelled') {
      return { isOverdue: false, isCriticalOverdue: false };
    }

    const deadlineDate = new Date(todo.deadline);
    const today = new Date();
    deadlineDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const overdue = today > deadlineDate;
    // Critical overdue: 2+ extensions means red, otherwise orange
    const extensionCount = (todo as any).extension_count ?? 0;
    const critical = overdue && extensionCount >= 2;

    return { isOverdue: overdue, isCriticalOverdue: critical };
  })();

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`border rounded-lg p-3 mb-2 cursor-move hover:shadow-md transition-shadow ${
        todo.status === 'completed' ? 'opacity-75' : ''
      } ${
        isCriticalOverdue ? 'bg-red-100' : isOverdue ? 'bg-orange-100' : 'bg-white'
      }`}
    >
      <div className="space-y-2">
        <div className="flex justify-between items-start">
          <h4 className={`font-medium text-sm ${todo.status === 'completed' ? 'line-through' : ''}`}>
            {todo.title}
          </h4>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onView(todo);
              }}
              className="h-6 w-6 p-0"
              title="View task details"
            >
              <Eye className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(todo);
              }}
              className="h-6 w-6 p-0"
              title="Edit task"
            >
              <Edit2 className="h-3 w-3" />
            </Button>
            {canDelete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(todo.id);
                }}
                className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                title="Delete task"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>

        {todo.description && (
          <div 
            className="text-xs text-gray-600 line-clamp-2 prose prose-sm max-w-none [&>p]:my-0"
            dangerouslySetInnerHTML={{ __html: todo.description }}
          />
        )}

        <div className="flex flex-wrap gap-1 items-center">
          {showStaffName && (
            <Badge variant="outline" className="text-xs">
              {getStaffName(todo.staff_id)}
            </Badge>
          )}
          <Badge className={`text-xs ${getPriorityColor(todo.priority)}`}>
            {todo.priority}
          </Badge>
          {todo.category && (
            <Badge variant="secondary" className="text-xs">
              {todo.category}
            </Badge>
          )}
          {todo.task_type && (
            <Badge variant="outline" className={`text-xs ${todo.task_type === 'Routine' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-orange-50 text-orange-700 border-orange-200'}`}>
              {todo.task_type}
            </Badge>
          )}
          {todo.project_id && (
            <Badge variant="outline" className="text-xs">
              {projects?.find(p => p.id === todo.project_id)?.name || 'Project'}
            </Badge>
          )}
          {todo.deadline && (
            <Badge variant="outline" className={`text-xs ${
              isCriticalOverdue ? 'bg-red-100 text-red-600 border-red-300' :
              isOverdue ? 'bg-orange-100 text-orange-600 border-orange-300' : ''
            }`}>
              <CalendarIcon className="h-3 w-3 mr-1" />
              {format(new Date(todo.deadline), 'MMM d')}
              {isOverdue && ' (Overdue)'}
            </Badge>
          )}
          {todo.completed_at && (
            <Badge variant="outline" className="text-xs text-green-600">
              <Clock className="h-3 w-3 mr-1" />
              {format(new Date(todo.completed_at), 'MMM d')}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};

const TasksKanbanView: React.FC<TasksKanbanViewProps> = ({
  todos,
  staff,
  onEdit,
  onDelete,
  onUpdateTodo,
  showStaffName = false,
  projects = [],
  canDelete = false,
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingTodo, setViewingTodo] = useState<StaffTodo | null>(null);
  const [lateReasonDialog, setLateReasonDialog] = useState<{
    show: boolean;
    task: StaffTodo | null;
    newStatus: TaskStatus | null;
  }>({ show: false, task: null, newStatus: null });
  const [lateReason, setLateReason] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleViewTodo = (todo: StaffTodo) => {
    setViewingTodo(todo);
    setViewModalOpen(true);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      setActiveId(null);
      return;
    }

    const activeTask = todos.find(todo => todo.id === active.id);
    // Check if we're over a column (not a task)
    const overColumn = statusColumns.find(col => col.status === over.id);

    if (activeTask && overColumn) {
      const newStatus = overColumn.status;
      if (activeTask.status !== newStatus) {
        // Check if task is being marked as completed and is late
        if (newStatus === 'completed' && activeTask.deadline) {
          const deadlineDate = new Date(activeTask.deadline);
          const completionDate = new Date();

          // Add 1 day to deadline to include full deadline day (per migration logic)
          const deadlinePlusOne = new Date(deadlineDate);
          deadlinePlusOne.setDate(deadlinePlusOne.getDate() + 1);
          deadlinePlusOne.setHours(0, 0, 0, 0);

          const completionDateNormalized = new Date(completionDate);
          completionDateNormalized.setHours(0, 0, 0, 0);

          const isLate = completionDateNormalized >= deadlinePlusOne;

          // If late, show dialog instead of immediately updating
          if (isLate) {
            setLateReasonDialog({
              show: true,
              task: activeTask,
              newStatus: newStatus,
            });
            setActiveId(null);
            return;
          }
        }

        // Not late or not being completed, update normally
        onUpdateTodo({
          ...activeTask,
          status: newStatus,
          completed_at: newStatus === 'completed' ? new Date().toISOString() :
                       activeTask.status === 'completed' ? null : activeTask.completed_at,
        });
      }
    }

    setActiveId(null);
  };

  const activeTodo = activeId ? todos.find(todo => todo.id === activeId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={rectIntersection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="overflow-x-auto pb-4 -mx-2 px-2">
        <div className="flex gap-3 sm:gap-4" style={{ minWidth: 'min-content' }}>
          {statusColumns.map(column => {
            const columnTodos = todos.filter(todo => todo.status === column.status);

            return (
              <div key={column.status} className="w-[260px] sm:w-80 flex-shrink-0">
                <Card className="h-full">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-sm">{column.title}</h3>
                      <Badge className={`${column.color} text-xs`}>
                        {columnTodos.length}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="overflow-y-auto max-h-[600px] px-3">
                    <DroppableColumn status={column.status}>
                      <SortableContext
                        items={columnTodos.map(todo => todo.id)}
                        strategy={verticalListSortingStrategy}
                        id={column.status}
                      >
                        {columnTodos.map(todo => (
                          <TaskCard
                            key={todo.id}
                            todo={todo}
                            staff={staff}
                            onEdit={onEdit}
                            onView={handleViewTodo}
                            onDelete={onDelete}
                            showStaffName={showStaffName}
                            isDragging={todo.id === activeId}
                            projects={projects}
                            canDelete={canDelete}
                          />
                        ))}
                        {columnTodos.length === 0 && (
                          <div className="text-center py-8 text-gray-400 text-sm">
                            Drop tasks here
                          </div>
                        )}
                      </SortableContext>
                    </DroppableColumn>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      </div>
      
      <DragOverlay>
        {activeTodo && (
          <div className="bg-white border-2 border-blue-500 rounded-lg p-3 shadow-lg opacity-90">
            <TaskCard
              todo={activeTodo}
              staff={staff}
              onEdit={onEdit}
              onView={handleViewTodo}
              onDelete={onDelete}
              showStaffName={showStaffName}
              projects={projects}
              canDelete={canDelete}
            />
          </div>
        )}
      </DragOverlay>
      
      {/* View Task Modal */}
      <ViewTaskModal
        isOpen={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setViewingTodo(null);
        }}
        todo={viewingTodo}
        staff={staff}
        projects={projects}
      />

      {/* Late Reason Dialog */}
      <Dialog
        open={lateReasonDialog.show}
        onOpenChange={(open) => {
          if (!open) {
            setLateReasonDialog({ show: false, task: null, newStatus: null });
            setLateReason('');
          }
        }}
      >
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Task Completed Late</DialogTitle>
            <DialogDescription>
              This task is being completed after its deadline. Please provide a reason.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Alert className="border-orange-500 bg-orange-50">
              <AlertCircle className="h-4 w-4 text-orange-600" />
              <AlertDescription>
                <div className="text-sm text-orange-900">
                  {lateReasonDialog.task && (
                    <>
                      <div className="font-semibold">{lateReasonDialog.task.title}</div>
                      <div className="mt-1">
                        Deadline: {lateReasonDialog.task.deadline ? format(new Date(lateReasonDialog.task.deadline), 'PPP') : 'N/A'}
                      </div>
                    </>
                  )}
                </div>
              </AlertDescription>
            </Alert>
            <div className="space-y-2">
              <Label htmlFor="kanban-late-reason">
                Reason for Late Completion <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="kanban-late-reason"
                value={lateReason}
                onChange={(e) => setLateReason(e.target.value)}
                placeholder="Explain why this task was completed late..."
                rows={4}
                maxLength={500}
                className="resize-none"
              />
              <div className="text-xs text-muted-foreground text-right">
                {lateReason.length}/500 characters
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setLateReasonDialog({ show: false, task: null, newStatus: null });
                setLateReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (lateReasonDialog.task && lateReasonDialog.newStatus) {
                  onUpdateTodo({
                    ...lateReasonDialog.task,
                    status: lateReasonDialog.newStatus,
                    completed_at: new Date().toISOString(),
                    late_reason: lateReason.trim(),
                  });
                  setLateReasonDialog({ show: false, task: null, newStatus: null });
                  setLateReason('');
                }
              }}
              disabled={!lateReason.trim()}
            >
              Complete Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DndContext>
  );
};

export default TasksKanbanView;